import React from 'react';
import classNames from 'classnames';
import { useIntl } from 'react-intl';
import InfoPage from '../InfoPage';
import { YoutubeEmbed } from '../../PageBuilder/Primitives/YoutubeEmbed';
import { AspectRatioWrapper, ExternalLink, H4, NamedLink } from '../../../components';
import { LOCALE, getContactEmailsForLocale, getCurrentLocale } from '../../../util/locale';
import { lazyLoadWithDimensions } from '../../../util/uiHelpers';
import { useConfiguration } from '../../../context/configurationContext';

import css from './HowItWorksPage.module.css';

const YT_VIDEO_ID = '48ekA83dR6M';

const Video = ({ children, ...props }) => <video {...props}>{children}</video>;
const LazyVideo = lazyLoadWithDimensions(Video);

const ListContent = props => {
  const { title, msgs, className } = props;

  return (
    <section className={classNames(css.listContent, className)}>
      <H4 as="h2">{title}</H4>
      <ul>
        {msgs.map((msg, index) => (
          <li key={index}>{msg}</li>
        ))}
      </ul>
    </section>
  );
};

const HowItWorksPage = props => {
  const { className } = props;

  const intl = useIntl();
  const config = useConfiguration();

  const classes = classNames(css.root, className);

  const locale = getCurrentLocale();

  const contactEmails = getContactEmailsForLocale(locale);

  const isBrazilLocale = locale === LOCALE.BRAZIL;
  const isUSLocale = locale === LOCALE.UNITED_STATES;

  return (
    <InfoPage
      title={intl.formatMessage({ id: 'General.howItWorks' })}
      contentTitle={intl.formatMessage({ id: 'General.howItWorks' })}
      currentPage="HowItWorksPage"
      mainColumnClassName={classes}
    >
      <ListContent
        title={intl.formatMessage({ id: 'General.functionalities' }) + ':'}
        msgs={
          isBrazilLocale
            ? [
                intl.formatMessage({ id: 'HowItWorksPage.functionalities1' }),
                intl.formatMessage({ id: 'HowItWorksPage.functionalities2' }),
              ]
            : [
                intl.formatMessage({ id: 'HowItWorksPage.functionalities1' }),
                intl.formatMessage({ id: 'HowItWorksPage.functionalities2' }),
                intl.formatMessage({ id: 'HowItWorksPage.functionalities3' }),
              ]
        }
      />

      {isBrazilLocale ? (
        <AspectRatioWrapper className={classNames(css.plainVideo, css.video)} width={16} height={9}>
          <LazyVideo
            className={css.plainVideoElement}
            style={{ background: '#000000' }}
            frameBorder="0"
            allow="accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
            title="Embedded video"
            controls
          >
            <source src="/static/videos/how-it-works-brazil.mp4" type="video/mp4" />
          </LazyVideo>
        </AspectRatioWrapper>
      ) : (
        <YoutubeEmbed youtubeVideoId={YT_VIDEO_ID} className={css.video} />
      )}

      <section>
        <H4 as="h2">{intl.formatMessage({ id: 'General.registration' }) + ':'}</H4>

        {intl.formatMessage(
          { id: 'HowItWorksPage.registrationDesc' },
          {
            signupLink: (
              <NamedLink name="SignupPage" className={css.signupLink}>
                {intl.formatMessage({ id: 'General.signUpHere' }).toLowerCase() + '.'}
              </NamedLink>
            ),
          }
        )}
        <br />
        <span className={css.registrationNote}>
          {intl.formatMessage({ id: 'HowItWorksPage.registrationNote' }, { emailLocale: locale })}
        </span>
      </section>

      <ListContent
        title={intl.formatMessage({ id: 'HowItWorksPage.sellingCriteria' }) + ':'}
        msgs={
          isBrazilLocale
            ? [
                intl.formatMessage({ id: 'HowItWorksPage.sellingCriteria1' }),
                intl.formatMessage({ id: 'HowItWorksPage.sellingCriteria2' }),
              ]
            : [
                intl.formatMessage({ id: 'HowItWorksPage.sellingCriteria1' }),
                intl.formatMessage({ id: 'HowItWorksPage.sellingCriteria2' }),
                intl.formatMessage({ id: 'HowItWorksPage.sellingCriteria3' }),
              ]
        }
      />

      <ListContent
        title={intl.formatMessage({ id: 'HowItWorksPage.userCommunication' }) + ':'}
        msgs={[
          intl.formatMessage({ id: 'HowItWorksPage.userCommunication1' }),
          intl.formatMessage({ id: 'HowItWorksPage.userCommunication2' }),
          intl.formatMessage({ id: 'HowItWorksPage.userCommunication3' }),
        ]}
      />
      {isUSLocale && (
        <p>
          {intl.formatMessage(
            { id: 'HowItWorksPage.downloadManual' },
            {
              manualLink: (
                <ExternalLink
                  href="https://jira-husqvarna.riada.se/secure/attachment/429022/429022_ReSpare+Training.pdf"
                  className={css.signupLink}
                >
                  {intl.formatMessage({ id: 'General.userManual' }).toLowerCase()}
                </ExternalLink>
              ),
            }
          )}
        </p>
      )}

      <p>{intl.formatMessage({ id: 'HowItWorksPage.pleaseSignUp' })}</p>
      <p>
        {intl.formatMessage(
          { id: 'HowItWorksPage.contactUs' },
          {
            contactEmail: contactEmails.map((email, i) => (
              <React.Fragment key={i}>
                <ExternalLink href={`mailto:${email}`} className={css.signupLink}>
                  {email}
                </ExternalLink>
                {i !== contactEmails.length - 1 && ', '}
              </React.Fragment>
            )),
          }
        )}
      </p>

      <p>
        {intl.formatMessage(
          { id: 'HowItWorksPage.reportViolations' },
          {
            reportLink: (
              <ExternalLink href={config.violationsUrl} className={css.signupLink}>
                {intl.formatMessage({ id: 'General.reportViolations' })}
              </ExternalLink>
            ),
          }
        )}
      </p>
    </InfoPage>
  );
};

export default HowItWorksPage;
